<script lang="ts">
	import { graphql, type UserAccountData$result } from '$houdini'

	import { onMount, type ComponentProps } from 'svelte'
	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import EditModeControls from 'components/search/EditModeControls.svelte'
	import type { SearchField } from 'utility/search-fields'
	import Pin from 'components/search/Pin.svelte'
	import type { SvelteSet } from 'svelte/reactivity'
	import type { i18n } from 'types/common'
	import { getContext } from 'svelte'
	const { t: translate } = getContext<i18n>('i18next')

	interface UserAccount extends Partial<UserAccountData$result['userAccounts'][0]> {
		id: UserAccountData$result['userAccounts'][0]['id']
		name: UserAccountData$result['userAccounts'][0]['name']
	}

	interface DisplayUserAccount extends UserAccount {
		displayName: string
	}

	const userAccountsQuery = graphql(`
		query UserAccountData {
			userAccounts {
				lastName
				id
				firstName
				name
				status
			}
		}
	`)

	interface Props extends Omit<
		ComponentProps<typeof Autocomplete<UserAccount | null>>,
		'options' | 'value' | 'emptyValue'
	> {
		initialValue?: number
		userAccount?: UserAccount | null
		userAccounts?: UserAccount[]
		label?: string
		searchField: SearchField
		inEditMode: boolean
		pinnedSearchFields: SvelteSet<SearchField['key']> //Required
		selectedSearchFieldsKeys: SvelteSet<SearchField['key']> //Required
	}

	let {
		initialValue = undefined,
		userAccount = $bindable(null),
		userAccounts = $bindable([]),
		label = translate('common:userAccount', 'User Account'),
		searchField,
		inEditMode,
		pinnedSearchFields = $bindable(),
		selectedSearchFieldsKeys = $bindable(),
		...rest
	}: Props = $props()

	function makeDisplayName(userAccount: UserAccount | DisplayUserAccount): string {
		return userAccount?.firstName && userAccount?.lastName
			? `${userAccount.firstName} ${userAccount.lastName}`
			: (userAccount?.name ?? '')
	}

	const displayUserAccounts: Array<DisplayUserAccount> = $derived(
		userAccounts
			.map(userAccount => {
				return {
					...userAccount,
					displayName: makeDisplayName(userAccount),
				}
			})
			.sort((a, b) => {
				const aStatus = a.status ?? ''
				const bStatus = b.status ?? ''
				if (aStatus === bStatus) {
					return a.displayName > b.displayName ? 1 : -1
				}
				return aStatus.localeCompare(bStatus)
			}),
	)

	onMount(async () => {
		userAccounts = (await userAccountsQuery.fetch({ policy: 'CacheOrNetwork' }))?.data?.userAccounts ?? []
		if (initialValue) {
			userAccount = userAccounts.find(account => account.id === initialValue) ?? null
		}
	})
</script>

<EditModeControls
	{searchField}
	{inEditMode}
	{pinnedSearchFields}
	{selectedSearchFieldsKeys}
>
	<Autocomplete
		{...rest}
		{label}
		emptyValue={null}
		options={displayUserAccounts}
		bind:value={userAccount}
		getLabel={option => {
			const account = option as DisplayUserAccount | null
			let displayName = account ? makeDisplayName(account) : ''
			return account?.status && account?.status.toLowerCase() !== 'active'
				? `${displayName} (${account.status.toLowerCase()})`
				: displayName
		}}
	>
		{#snippet hint()}
			<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
		{/snippet}
	</Autocomplete>
</EditModeControls>
